/**
 * PDF Export Button Component
 * Provides export and share functionality for PhiCor reports
 */

import React, { useState } from 'react';
import { Button } from './ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import { FileText, Download, Share2, Mail, MessageCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  generateFoodAnalysisPDF,
  generateDrugAnalysisPDF,
  generateDiagnosisPDF,
  generateDiseaseAnalysisPDF,
  downloadPDF,
  sharePDF,
  getPDFBlob
} from '../utils/pdfExport';

const PDFExportButton = ({ 
  type = 'food', // 'food', 'drug', 'diagnosis', 'disease'
  data,
  patientProfile = null,
  patientInput = '', // For diagnosis
  className = '',
  variant = 'outline',
  size = 'sm',
  isDark = true
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  
  const getFilename = () => {
    const date = new Date().toISOString().split('T')[0];
    const name = data?.product_name || data?.name || type;
    const safeName = name.replace(/[^a-z0-9]/gi, '_').substring(0, 30);
    return `PhiCor_${type}_${safeName}_${date}.pdf`;
  };
  
  const generatePDF = () => {
    switch (type) {
      case 'food':
        return generateFoodAnalysisPDF(data, patientProfile);
      case 'drug':
        return generateDrugAnalysisPDF(data, patientProfile);
      case 'diagnosis':
        return generateDiagnosisPDF(data, patientInput, patientProfile);
      case 'disease':
        return generateDiseaseAnalysisPDF(data, patientProfile);
      default:
        return generateFoodAnalysisPDF(data, patientProfile);
    }
  };
  
  const handleDownload = async () => {
    setIsGenerating(true);
    try {
      const doc = generatePDF();
      downloadPDF(doc, getFilename());
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF generation failed:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleShare = async () => {
    setIsGenerating(true);
    try {
      const doc = generatePDF();
      const result = await sharePDF(doc, getFilename(), `PhiCor ${type.charAt(0).toUpperCase() + type.slice(1)} Analysis`);
      
      if (result.success && !result.fallback) {
        toast.success('Report shared successfully');
      } else if (result.fallback === 'download') {
        toast.info('Sharing not supported - PDF downloaded instead');
      } else if (result.cancelled) {
        // User cancelled, no toast needed
      } else if (!result.success) {
        toast.error('Failed to share: ' + result.error);
      }
    } catch (error) {
      console.error('Share failed:', error);
      toast.error('Failed to share report');
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleEmailShare = async () => {
    setIsGenerating(true);
    try {
      const doc = generatePDF();
      const blob = getPDFBlob(doc);
      
      // Create a data URL for the PDF
      const reader = new FileReader();
      reader.onload = () => {
        const subject = encodeURIComponent(`PhiCor ${type.charAt(0).toUpperCase() + type.slice(1)} Analysis Report`);
        const body = encodeURIComponent(`Please find attached my PhiCor health analysis report.\n\nGenerated by PhiCor.ai - Mathematical Health Intelligence Platform`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
        
        // Also trigger download so user can attach
        downloadPDF(doc, getFilename());
        toast.info('PDF downloaded - attach it to your email');
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      toast.error('Failed to prepare email');
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleWhatsAppShare = async () => {
    setIsGenerating(true);
    try {
      const doc = generatePDF();
      downloadPDF(doc, getFilename());
      
      const text = encodeURIComponent(`Check out my PhiCor ${type} analysis report! Generated by PhiCor.ai - Mathematical Health Intelligence Platform`);
      window.open(`https://wa.me/?text=${text}`, '_blank');
      toast.info('PDF downloaded - share via WhatsApp');
    } catch (error) {
      toast.error('Failed to share');
    } finally {
      setIsGenerating(false);
    }
  };
  
  if (!data) return null;
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size} 
          className={`gap-2 ${className}`}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileText className="w-4 h-4" />
          )}
          Export PDF
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={isDark ? 'bg-gray-900 border-gray-800' : ''}>
        <DropdownMenuItem 
          onClick={handleDownload}
          className={`gap-2 cursor-pointer ${isDark ? 'hover:bg-gray-800 focus:bg-gray-800' : ''}`}
        >
          <Download className="w-4 h-4" />
          Download PDF
        </DropdownMenuItem>
        
        <DropdownMenuSeparator className={isDark ? 'bg-gray-800' : ''} />
        
        <DropdownMenuItem 
          onClick={handleShare}
          className={`gap-2 cursor-pointer ${isDark ? 'hover:bg-gray-800 focus:bg-gray-800' : ''}`}
        >
          <Share2 className="w-4 h-4" />
          Share...
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={handleEmailShare}
          className={`gap-2 cursor-pointer ${isDark ? 'hover:bg-gray-800 focus:bg-gray-800' : ''}`}
        >
          <Mail className="w-4 h-4" />
          Share via Email
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={handleWhatsAppShare}
          className={`gap-2 cursor-pointer ${isDark ? 'hover:bg-gray-800 focus:bg-gray-800' : ''}`}
        >
          <MessageCircle className="w-4 h-4" />
          Share via WhatsApp
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default PDFExportButton;
